import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import Logo from "@/components/Logo";
import { api, track, formatApiError } from "@/lib/api";
import { IMAGES } from "@/lib/images";

export default function Invitation() {
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    track("invitation_started", { code_len: code.length });
    try {
      const { data } = await api.post("/invitations/validate", { code });
      track("invitation_valid", {});
      navigate("/apply", { state: { invitation_id: data.invitation_id, invitation_source: data.source } });
    } catch (err) {
      track("invitation_invalid", {});
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-[100svh] bg-navy-900 flex flex-col items-center justify-center px-6 py-20 overflow-hidden" data-testid="invitation-page">
      <img src={IMAGES.positanoDusk} alt="" className="absolute inset-0 w-full h-full object-cover opacity-15" />
      <div className="absolute inset-0 bg-navy-900/85" />

      <Link to="/" className="absolute top-8 left-8 text-ivory/50 hover:text-gold transition-colors inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase z-10" data-testid="invitation-back">
        <ArrowLeft size={15} /> Return
      </Link>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-10 w-full max-w-lg text-center"
      >
        <Logo variant="full" className="h-24 mx-auto" />

        <h1 className="mt-14 font-serif font-light text-3xl md:text-5xl text-ivory leading-tight tracking-tight">
          Welcome to VillaFlow Private Collection.
        </h1>
        <p className="mt-6 text-ivory/60 font-light leading-relaxed">
          Your invitation gives you access to the VillaFlow property selection process.
        </p>

        <form onSubmit={submit} className="mt-14">
          <label className="block text-left text-[11px] tracking-[0.3em] uppercase text-gold mb-4">Invitation Code</label>
          <input
            value={code}
            onChange={(e) => { setCode(e.target.value.toUpperCase()); setError(""); }}
            placeholder="VF-AMALFI-XXXX"
            data-testid="invitation-code-input"
            className="w-full bg-transparent border-b border-ivory/25 focus:border-gold outline-none py-4 text-center text-ivory text-xl md:text-2xl tracking-[0.25em] font-light placeholder:text-ivory/25 transition-colors duration-500"
          />
          {error && <p className="mt-4 text-sm text-gold/90 font-light" data-testid="invitation-error">{error}</p>}

          <button
            type="submit"
            disabled={loading || !code.trim()}
            data-testid="invitation-continue-btn"
            className="group mt-12 inline-flex items-center gap-3 border border-gold/50 hover:border-gold text-ivory hover:text-gold px-12 py-4 text-xs tracking-[0.25em] uppercase transition-colors duration-500 disabled:opacity-40"
          >
            {loading ? "Verifying" : "Continue"}
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-500" />
          </button>
        </form>

        <p className="mt-16 text-ivory/40 text-xs font-light">
          No invitation?{" "}
          <Link to="/apply" className="text-gold/80 hover:text-gold underline underline-offset-4" data-testid="invitation-request-link">
            Request consideration
          </Link>
        </p>
      </motion.div>
    </div>
  );
}
